import React, { FC, useMemo } from "react";
import { AppPage } from "shared-ts-ui-app-page";
import { Card, Container, Divider, Stack, Typography } from "@mui/material";
import { BuildingTreeView } from "./BuildingTreeView";
import { AddressId } from "firefly-shared-ts-domain";
import {
  BuildingSelect,
  Chimney,
  Fireplace,
  Flue,
} from "firefly-my-chimney-ts-building";
import { useTranslation } from "react-i18next";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import { ChimneyPanel } from "./ChimneyPanel";
import { FireplacePanel } from "./FireplacePanel";
import { FluePanel } from "./FluePanel";
import {
  NullableDictionarySetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { IllegalStateError } from "shared-ts-utils-core";
import { createLazyRoute } from "@tanstack/react-router";
import { useMyChimneyContext } from "my-chimney-ts-my-chimney-context";

export const CombustionPlantConfigurationPage: FC = (props) => {
  const {
    buildings,
    selectedBuilding,
    setSelectedBuilding,
    onChimneyUpdate,
    onFlueUpdate,
    onFireplaceUpdate,
  } = useMyChimneyContext();
  const { t } = useTranslation("my-chimney-ts-translations");
  const [selectedObjectId, setSelectedObjectId] = useUserSettingWithDefault<{
    id: string;
    type: "Chimney" | "Flue" | "Fireplace";
  } | null>(
    `SettingsPage.${selectedBuilding.id}.selectedObject`,
    NullableDictionarySetting,
    null
  );

  const selectedObject = useMemo(() => {
    if (selectedObjectId == null) {
      return undefined;
    } else if (selectedObjectId?.type === "Chimney") {
      return selectedBuilding.findChimneyById(selectedObjectId.id);
    } else if (selectedObjectId?.type === "Flue") {
      return selectedBuilding.findFlueById(selectedObjectId.id);
    } else if (selectedObjectId?.type === "Fireplace") {
      return selectedBuilding.findFireplaceById(selectedObjectId.id);
    } else {
      throw new IllegalStateError(
        `selectedObjectId.type not supported: ${selectedObjectId?.type}`
      );
    }
  }, [selectedBuilding, selectedObjectId]);

  const handleSelectedChimney = (chimney: Chimney) => {
    setSelectedObjectId({
      id: chimney.id,
      type: chimney.type,
    });
  };

  const handleSelectedFlue = (flue: Flue) => {
    setSelectedObjectId({
      id: flue.id,
      type: flue.type,
    });
  };

  const handleSelectedFireplace = (fireplace: Fireplace) => {
    setSelectedObjectId({
      id: fireplace.id,
      type: fireplace.type,
    });
  };

  const handleDwellingChange = (value: AddressId) => {
    setSelectedBuilding(value);
  };

  return (
    <AppPage
      $key={"CombustionPlantConfigurationPage"}
      name={t("Combustion Plant")}
      icon={SettingsRoundedIcon}
      mainPaneActions={
        <BuildingSelect
          buildings={buildings}
          selected={selectedBuilding.id}
          onChange={handleDwellingChange}
        />
      }
      mainPaneProps={{
        scrollableContentVertically: true,
      }}
      mainPane={
        <Container maxWidth={"xs"} sx={{ flexGrow: 1, minHeight: 0 }}>
          <Card sx={{ p: 2 }}>
            <Typography sx={{ mt: 1 }}>
              {t("Choose object to configure")}:
            </Typography>
            <Divider />
            <BuildingTreeView
              sx={{ flexGrow: 1 }}
              building={selectedBuilding}
              selectedObject={selectedObjectId}
              onSelectedFireplace={handleSelectedFireplace}
              onSelectedFlue={handleSelectedFlue}
              onSelectedChimney={handleSelectedChimney}
            />
          </Card>
        </Container>
      }
      supportingPaneProps={{
        label: t("Details"),
        openByDefault: true,
        resizePanelProps: {
          horizontal: {
            minWidth: 300,
            maxWidth: 600,
          },
          vertical: {
            minHeight: 150,
            maxHeight: 600,
          },
          mobile: {
            maxHeight: 400,
            minHeight: 150,
          },
        },
      }}
      supportingPane={
        <Stack direction={"column"} gap={1} useFlexGap sx={{ flexGrow: 1 }}>
          <Card sx={{ p: 2, flexGrow: 1 }}>
            {selectedObject !== undefined &&
              selectedObject.type === "Chimney" && (
                <ChimneyPanel
                  chimney={selectedObject}
                  environment={selectedBuilding.environment.id}
                  onChange={onChimneyUpdate}
                />
              )}
            {selectedObject !== undefined && selectedObject.type === "Flue" && (
              <FluePanel
                flue={selectedObject}
                environment={selectedBuilding.environment.id}
                onChange={onFlueUpdate}
              />
            )}
            {selectedObject !== undefined &&
              selectedObject.type === "Fireplace" && (
                <FireplacePanel
                  fireplace={selectedObject}
                  environment={selectedBuilding.environment.id}
                  onChange={onFireplaceUpdate}
                />
              )}
          </Card>
        </Stack>
      }
    />
  );
};

export const CombustionPlantPageRoute = createLazyRoute(
  "/_myChimneyContext/combustion-plant"
)({
  component: CombustionPlantConfigurationPage,
});
